import { useParams, useNavigate, Link } from 'react-router-dom';
import { Table, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { PageContent } from '@/components/layout/PageLayout';
import DetailHeader, { DetailTitle } from '@/components/layout/DetailHeader';
import { Section, SectionTitle } from '@/components/layout/Toolbar.styled';
import { ClickableTr } from '@/styles/shared.styled';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useGetPelnomocnikQuery } from '@/store/pelnomocnicyApi';
import { DirectoryStatusBadge } from '@/components/pelnomocnicy/DirectoryStatusBadge';

export default function PelnomocnikDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: pelnomocnik, isLoading } = useGetPelnomocnikQuery(id!, { skip: !id });

  if (isLoading || !pelnomocnik) {
    return <PageContent><LoadingSpinner /></PageContent>;
  }

  return (
    <PageContent>
      <DetailHeader
        onBack={() => navigate(-1)}
        title={
          <div className="d-flex align-items-center gap-2">
            <DetailTitle>{pelnomocnik.name}</DetailTitle>
            <DirectoryStatusBadge
              status={pelnomocnik.directoryStatus}
              professionalType={pelnomocnik.directoryProfessionalType}
            />
          </div>
        }
        meta={pelnomocnik.address ? <span>{pelnomocnik.address}</span> : undefined}
      />

      {pelnomocnik.directoryProfessionalType && (
        <Section>
          <div className="p-3 border rounded bg-light d-flex flex-wrap gap-4">
            {pelnomocnik.directoryRegistryNumber && (
              <div>
                <small className="text-muted d-block">{t('pelnomocnicy.directory.registryNumber')}</small>
                <span className="fw-semibold">{pelnomocnik.directoryRegistryNumber}</span>
              </div>
            )}
            <div>
              <small className="text-muted d-block">{t('pelnomocnicy.table.type')}</small>
              <span className="fw-semibold">
                {t(`pelnomocnicy.directory.${pelnomocnik.directoryProfessionalType}`)}
              </span>
            </div>
            {pelnomocnik.directoryEmail && (
              <div>
                <small className="text-muted d-block">{t('pelnomocnicy.directory.email')}</small>
                <a href={`mailto:${pelnomocnik.directoryEmail}`}>{pelnomocnik.directoryEmail}</a>
              </div>
            )}
            {pelnomocnik.directoryProfileUrl && (
              <div>
                <small className="text-muted d-block">{t('pelnomocnicy.directory.profileUrl')}</small>
                <a href={pelnomocnik.directoryProfileUrl} target="_blank" rel="noopener noreferrer">
                  {t('pelnomocnicy.directory.profileUrl')}
                </a>
              </div>
            )}
          </div>
        </Section>
      )}

      <Section>
        <SectionTitle>{t('pelnomocnicy.sprawy')} ({pelnomocnik.sprawy.length})</SectionTitle>
        {!pelnomocnik.sprawy.length ? (
          <p className="text-muted">{t('pelnomocnicy.noSprawy')}</p>
        ) : (
          <Table hover responsive>
            <thead>
              <tr>
                <th>{t('sprawy.table.signature')}</th>
                <th>{t('sprawy.table.court')}</th>
                <th>{t('sprawy.form.subject')}</th>
                <th>{t('sprawy.table.status')}</th>
              </tr>
            </thead>
            <tbody>
              {pelnomocnik.sprawy.map((s) => (
                <ClickableTr key={s.id} onClick={() => navigate(`/sprawy/${s.id}`)}>
                  <td className="fw-semibold"><Link to={`/sprawy/${s.id}`}>{s.signature}</Link></td>
                  <td>{s.court ?? '—'}</td>
                  <td>{s.subject ?? '—'}</td>
                  <td><Badge bg="secondary">{t(`sprawaStatus.${s.portalStatus}`)}</Badge></td>
                </ClickableTr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </PageContent>
  );
}
